import PropTypes from "prop-types";
import ReactApexChart from "react-apexcharts";
import { useState } from "react";
import {
  Card,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useChart } from "../chart";
import { get } from "../../utils/networkUtils";

WinRatioChart.propTypes = {
  title: PropTypes.string,
  subheader: PropTypes.string,
  userId: PropTypes.number.isRequired,
  height: PropTypes.number,
};

export default function WinRatioChart({
  title,
  subheader,
  userId,
  height = 364,
  ...other
}) {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);

    if (isExpanded && chartData.length === 0) {
      fetchWinRatioData();
    }
  };

  const fetchWinRatioData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await get(`/admin/versus/games/win-ratio`);

      if (response.success && response.data) {
        const labels = response.data.map((item) => item.date);
        const data = response.data.map((item) => item.winRatio);

        setChartLabels(labels);
        setChartData([
          {
            name: "Win Ratio",
            data: data,
            fill: "solid",
          },
        ]);
      } else {
        setError("No data available");
      }
    } catch (err) {
      console.error("Error fetching win ratio data:", err);
      setError("Failed to load win ratio data");
    } finally {
      setLoading(false);
    }
  };

  const chartOptions = useChart({
    plotOptions: {
      bar: {
        columnWidth: "36%",
        borderRadius: 4,
      },
    },
    fill: { type: chartData.map((i) => i.fill) },
    labels: chartLabels,
    xaxis: {
      categories: chartLabels,
      title: {
        text: "Date",
      },
    },
    yaxis: {
      title: {
        text: "Win Ratio",
      },
      min: 0,
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (y) => {
          if (typeof y !== "undefined") {
            return `${y.toFixed(2)}`;
          }
          return y;
        },
      },
    },
    colors: ["#00AB55"],
  });

  return (
    <Card {...other}>
      <Accordion expanded={expanded} onChange={handleChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="win-ratio-chart-content"
          id="win-ratio-chart-header"
        >
          <Typography variant="h6">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : chartData.length > 0 ? (
            <Box sx={{ p: 1, pb: 0 }} dir="ltr">
              <ReactApexChart
                type="bar"
                series={chartData}
                options={chartOptions}
                height={height}
              />
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    </Card>
  );
}
